/**
 * ModuleHome — per-module landing: card grid of the module's nav entries.
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Card, CardActionArea, CardContent, Grid, Typography } from '@mui/material';
import {
  MODULE_CONFIGS,
  navigationConfig,
  type ModuleId,
  type NavigationItem,
} from '../registry/portalRegistry';

interface ModuleHomeProps {
  moduleId: string;
}

function flatten(items: NavigationItem[]): NavigationItem[] {
  return items.flatMap((it) => (it.children?.length ? flatten(it.children) : [it]));
}

const ModuleHome: React.FC<ModuleHomeProps> = ({ moduleId }) => {
  const navigate = useNavigate();
  const config = MODULE_CONFIGS[moduleId as ModuleId];
  const section = navigationConfig.find((s) => s.id === moduleId);
  if (!config) return <Typography>Module not found</Typography>;

  const leaves = section ? flatten(section.children) : [];

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', py: 2 }}>
      <Typography variant="h5" fontWeight={700} sx={{ color: config.accent, mb: 0.5 }}>
        {config.label}
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        {config.description}
      </Typography>
      <Grid container spacing={1.5}>
        {leaves.map((item) => (
          <Grid size={{ xs: 12, sm: 6, md: 4 }} key={item.path}>
            <Card variant="outlined" sx={{ '&:hover': { borderColor: config.accent } }}>
              <CardActionArea onClick={() => navigate(item.path)}>
                <CardContent sx={{ py: 1.5 }}>
                  <Typography variant="body2" fontWeight={600}>
                    {item.label}
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default ModuleHome;
